import { forwardRef, type ComponentType, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Spójny badge używany w całym /panel.
 * - jednolite zaokrąglenie (pill), wysokość, padding, typografia
 * - 5 wariantów kolorystycznych dopasowanych do palety projektu
 * - opcjonalny tooltip wyświetlany po najechaniu / focusie
 */
export type PanelBadgeVariant =
  | "verified" // mocny zielony (potwierdzenie organizacji)
  | "active"   // miękki zielony (aktualne dane)
  | "urgent"   // pomarańcz/czerwień (pilne potrzeby)
  | "primary"  // delikatny zielony akcent (np. role)
  | "neutral"; // szary, najbardziej stonowany

const VARIANT_CLASSES: Record<PanelBadgeVariant, string> = {
  verified: "bg-primary/90 text-primary-foreground border border-transparent",
  active: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  urgent: "bg-destructive text-destructive-foreground border border-transparent",
  primary: "bg-primary/10 text-primary border border-primary/20",
  neutral: "bg-muted text-muted-foreground border border-border/60",
};

interface PanelBadgeProps {
  variant?: PanelBadgeVariant;
  icon?: ComponentType<{ className?: string }>;
  tooltip?: ReactNode;
  className?: string;
  children: ReactNode;
}

export const PanelBadge = forwardRef<HTMLSpanElement, PanelBadgeProps>(
  ({ variant = "neutral", icon: Icon, tooltip, className, children }, ref) => {
    const badge = (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1 rounded-full h-5 px-2 text-[11px] font-medium leading-none whitespace-nowrap",
          VARIANT_CLASSES[variant],
          className
        )}
      >
        {Icon && <Icon className="h-3 w-3 shrink-0" />}
        {children}
      </span>
    );

    if (!tooltip) return badge;

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-center">
            {typeof tooltip === "string" ? <p>{tooltip}</p> : tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

PanelBadge.displayName = "PanelBadge";
